import { IRecipient, IDonor, ILkdpiResults, ISingleEpletMmRisk } from '@nkr/models/index';
import { Panel, OverlayTrigger, Popover, Glyphicon, Label } from 'react-bootstrap';
import { TableWrapper } from './styles';
import PopoverTemplate from '@nkr/components/popover-template';
import SingleEpletMmRisk from '../swap-management/single-eplet-mm-risk';

interface IProps {
    donor: IDonor,
    recipient: IRecipient,
    lkdpiResults: ILkdpiResults,
    singleEpletMmRisk: ISingleEpletMmRisk,
    compatiblePairLkdpiResults: ILkdpiResults
}

const DonorRecipientInfo = (props: IProps) => {
    const { donor, recipient } = props;

    return (
        <Panel>
            <Panel.Heading><span className='heading medium'>Donor &amp; Recipient Info</span></Panel.Heading>
            <Panel.Body>
                <TableWrapper striped condensed className='donor-recipient-info'>
                    <colgroup>
                        <col style={{ width: '38%' }} />
                        <col style={{ width: '31%' }} />
                        <col style={{ width: '31%' }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th></th>
                            <th>Donor</th>
                            <th>Recipient</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Alias</td>
                            <td><span>{donor.alias}</span></td>
                            <td><span>{recipient.alias}</span></td>
                        </tr>
                        <tr>
                            <td>Center</td>
                            <td><span>{donor.center.alias}{donor.rdnCenterId > 0 && (<><br />(<span style={{ fontSize: 12, color: 'red' }}>{donor.rdnCenterAlias}</span>)</>)}</span></td>
                            <td><span>{recipient.center.alias}</span></td>
                        </tr>
                        <tr>
                            <td>ABO</td>
                            <td><Label>{donor.fullBloodType}</Label></td>
                            <td><Label>{recipient.bloodType}</Label></td>
                        </tr>
                        <tr>
                            <td>Age</td>
                            <td><Label>{donor.age}</Label></td>
                            <td><Label>{recipient.age}</Label></td>
                        </tr>
                        <tr>
                            <td>Weight (kg)</td>
                            <td><Label>{donor.weight}</Label></td>
                            <td><Label>{recipient.weight}</Label></td>
                        </tr>
                        <tr>
                            <td>Height (cm)</td>
                            <td><Label>{donor.height}</Label></td>
                            <td><Label>{recipient.height}</Label></td>
                        </tr>
                        <tr>
                            <td>Gender</td>
                            <td><Label>{donor.gender}</Label></td>
                            <td><Label>{recipient.gender}</Label></td>
                        </tr>
                        <tr>
                            <td>Race</td>
                            <td>{donor.race ? <Label>{donor.race}</Label> : <Label className='no-value'>--</Label>}</td>
                            <td>{recipient.race ? <Label>{recipient.race}</Label> : <Label className='no-value'>--</Label>}</td>
                        </tr>
                        <tr>
                            <td>Blood Pressure</td>
                            <td>{donor.sbp && donor.dbp ? <Label>{donor.sbp} / {donor.dbp}</Label> : <Label className='no-value'>--</Label>}</td>
                            <td><Label className='no-value'>--</Label></td>
                        </tr>
                        <tr>
                            <td>CrCl</td>
                            <td><Label>{donor.creatinineClearance}</Label></td>
                            <td><Label className='no-value'>--</Label></td>
                        </tr>
                        <tr>
                            <td>CrCl Collection Method</td>
                            <td><Label>{donor.creatinineCollectionMethod}</Label></td>
                            <td><Label className='no-value'>--</Label></td>
                        </tr>
                        <tr>
                            <td>Serum Creatinine</td>
                            <td>{donor.serumCreatinine ? <Label>{Math.round(donor.serumCreatinine * 100) / 100}</Label> : <Label className='no-value'>--</Label>}</td>
                            <td><Label className='no-value'>--</Label></td>
                        </tr>
                        <tr>
                            <td>eGFR (2009)</td>
                            <td>{props.lkdpiResults?.gfr ? <Label>{Math.round(props.lkdpiResults.gfr)}</Label> : <Label className='no-value'>--</Label>}</td>
                            <td><Label className='no-value'>--</Label></td>
                        </tr>
                        <tr>
                            <td>eGFR (2021)</td>
                            <td>{donor.estimatedGfr2021 ? <Label>{Math.round(donor.estimatedGfr2021)}</Label> : <Label className='no-value'>--</Label>}</td>
                            <td><Label className='no-value'>--</Label></td>
                        </tr>
                        <tr>
                            <td>
                                <span style={{ marginRight: 5 }}>LKDPI</span>
                                <OverlayTrigger trigger='click' placement='top' rootClose overlay={
                                    <PopoverTemplate>
                                        <p className='text-center'><b>Donor-Recipient matches with a lower LKDPI are associated with a lower risk of graft loss</b></p>
                                        <p className='text-center'>Research can be found at:</p>
                                        <p className='text-center'><a href='https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6114098/' target='_blank'>A Risk Index for Living Donor Kidney Transplantation</a></p>
                                    </PopoverTemplate>}>
                                    <Glyphicon glyph='info-sign' />
                                </OverlayTrigger>
                            </td>
                            <td>{props.lkdpiResults.index != null ? <Label>{props.lkdpiResults.index}</Label> : <>--</>}</td>
                            <td><Label className='no-value'>--</Label></td>
                        </tr>

                        {props.compatiblePairLkdpiResults != null && (
                            <tr>
                                <td>
                                    <span style={{ marginRight: 5 }}>Compatible Donor's LKDPI</span>
                                    <OverlayTrigger trigger='click' placement='top' rootClose overlay={
                                        <PopoverTemplate>
                                            <p className='text-center'><b>Donor-Recipient matches with a lower LKDPI are associated with a lower risk of graft loss</b></p>
                                            <p className='text-center'>Research can be found at:</p>
                                            <p className='text-center'><a href='https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6114098/' target='_blank'>A Risk Index for Living Donor Kidney Transplantation</a></p>
                                        </PopoverTemplate>}>
                                        <Glyphicon glyph='info-sign' />
                                    </OverlayTrigger>
                                </td>
                                <td><Label>{props.compatiblePairLkdpiResults.index}</Label></td>
                                <td><Label className='no-value'>--</Label></td>
                            </tr>
                        )}

                        <tr>
                            <td>Eplet MM Risk</td>
                            <td><SingleEpletMmRisk data={props.singleEpletMmRisk} isAdmin={false} centerId={props.recipient.centerId} /></td>
                            <td><Label className='no-value'>--</Label></td>
                        </tr>
                    </tbody>
                </TableWrapper>
            </Panel.Body>
        </Panel>
    );
}

export default DonorRecipientInfo;